import React, { useEffect, useRef, useState } from 'react';
import API from '../../utils/ApiConfig';
import { useTranslation } from 'react-i18next';
import SectionHeader from '../common/SectionHeader';
import SectionFooter from '../common/SectionFooter/SectionFooter';
import TooltipComponent from '../common/Tooltip/Tooltip';
import DropdownPM from '../common/DropdownPM/DropdownPM';
import {
  GET_FORECAST_ACTUALS_END_PERIODS,
  GET_FORECAST_ACTUALS_VERSIONS,
  GET_FORECAST_BUDGET_VERSIONS,
  GET_PLAN_VERSION,
  POST_FORECAST,
  UPDATE_FORECAST,
} from '../../utils/ApiEndpoints';
import ApiErrorMessage from '../common/ErrorMessage/ApiErrorMessage';
import InputSwitchPM from '../common/InputSwitchPM/InputSwitchPM';
import Loader from '../common/Loader/Loader';

export default function ForecastEditor(props) {
  const { t } = useTranslation('forecast');
  const [plan, setPlan] = useState({});
  const [securityEnabled, setSecurityEnabled] = useState(false);
  const [formValid, setFormValid] = useState(false);
  const [formChanged, setFormChanged] = useState(false);
  const [buttonConfig, setButtonConfig] = useState([]);
  const [error, setError] = useState();
  const [actuals, setActuals] = useState(undefined);
  const [budgets, setBudgets] = useState(undefined);
  const [actualsEndPeriods, setActualsEndPeriod] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [forecast, setForecast] = useState({
    name: '',
    description: '',
    actualsVersionId: '',
    budgetVersionId: '',
    forecastSecurity: '',
    control: '',
    isRollingForecast: true,
    endPeriodActuals: '',
  });
  const formInitialState = useRef({});
  const securityDropdownValues = useRef();

  const forecastService = props.pageParams.services.forecastService;
  const CommonService = props.services.CommonService;

  const saveForecast = forecast => API.post(POST_FORECAST, { ...forecast });
  const updateForecast = forecast => API.post(UPDATE_FORECAST, { ...forecast });
  const getActualsVersions = planId => API.get(GET_FORECAST_ACTUALS_VERSIONS(planId));
  const getBudgetVersions = planId => API.get(GET_FORECAST_BUDGET_VERSIONS(planId));
  const getActualsEndPeriods = (planId, forecastDefinitionId) => API.get(GET_FORECAST_ACTUALS_END_PERIODS(planId, forecastDefinitionId));
  const getPlanVersion = planId => API.get(GET_PLAN_VERSION(planId));

  // init
  useEffect(() => {
    const planParam = props.pageParams.data.plan;
    formInitialState.current = forecastService.DATA.forecast;
    setPlan(planParam);
    getPlanVersion(planParam.mongoid).then(response => {
      setSecurityEnabled(response.data.enableMemberSecurity);
    });
    setFormValid(isEditOfForecast());
    setupDropdown(planParam.mongoid);
    loadFormValues(planParam.mongoid);
  }, []);

  // called when form validity state changes
  useEffect(() => {
    setupButtons(!formValid);
  }, [formValid]);

  // called when form state changes
  useEffect(() => {
    forecastService.setFormChange(formChanged);
  }, [formChanged]);

  // called when budgets dropdown values are loaded
  useEffect(() => {
    if (budgets && !budgets.some(v => v.value === formInitialState.current.budgetVersionId)) {
      formInitialState.current.budgetVersionId = '';
      setForecast(previous => ({ ...previous, budgetVersionId: '' }));
      setFormValid(false);
    }
  }, [budgets]);

  // called when actuals dropdown values are loaded
  useEffect(() => {
    if (actuals && !actuals.some(v => v.value === formInitialState.current.actualsVersionId)) {
      formInitialState.current.actualsVersionId = '';
      setForecast(previous => ({ ...previous, actualsVersionId: '' }));
      setFormValid(false);
    }
  }, [actuals]);

  function setupDropdown(planId) {
    getBudgetVersions(planId)
      .then(mapToDropdown)
      .then(data => setBudgets(data));
    getActualsVersions(planId)
      .then(mapToDropdown)
      .then(data => setActuals(data));
    if (formInitialState.current.isCopyMode) {
      getActualsEndPeriods(planId, formInitialState.current.originalForecastId).then(response => setActualsEndPeriod(response.data));
    } else if (formInitialState.current.id) {
      getActualsEndPeriods(planId, formInitialState.current.id).then(response => setActualsEndPeriod(response.data));
    } else {
      getActualsEndPeriods(planId).then(response => setActualsEndPeriod(response.data));
    }
    securityDropdownValues.current =
      CommonService.auth.getCurrentUser().userType === 'ACCOUNTADMIN' || CommonService.auth.getCurrentUser().userType === 'CENTAGEEMPLOYEE'
        ? [
            { label: t('FORECAST_FORM_COPY_SECURITY_ADMIN'), value: 'ADMIN' },
            { label: t('FORECAST_FORM_COPY_SECURITY_ACTUALS'), value: 'ACTUALS' },
            { label: t('FORECAST_FORM_COPY_SECURITY_BUDGET'), value: 'BUDGET' },
          ]
        : [
            { label: t('FORECAST_FORM_COPY_SECURITY_ACTUALS'), value: 'ACTUALS' },
            { label: t('FORECAST_FORM_COPY_SECURITY_BUDGET'), value: 'BUDGET' },
          ];
  }

  function mapToDropdown(response) {
    return response.data.map(version => {
      return {
        label: version.name,
        value: version.id,
      };
    });
  }

  function setupButtons(saveDisabled) {
    if (isEditOfForecast()) {
      setButtonConfig([
        {
          id: 1,
          primary: true,
          name: t('FORECAST_FORM_BUTTON_SAVE'),
          blockUi: true,
          disabled: saveDisabled,
        },
        {
          id: 2,
          primary: false,
          name: t('FORECAST_FORM_BUTTON_CANCEL'),
          blockUi: true,
        },
      ]);
    } else {
      setButtonConfig([
        {
          id: 0,
          primary: true,
          name: t('FORECAST_FORM_BUTTON_CREATE'),
          blockUi: true,
          disabled: saveDisabled,
        },
        {
          id: 2,
          primary: false,
          name: t('FORECAST_FORM_BUTTON_CANCEL'),
          blockUi: true,
        },
      ]);
    }
  }

  function loadFormValues(planId) {
    if (!isEditOfForecast()) {
      formInitialState.current.planId = planId;
    } else {
      if (isReadAccess(formInitialState.current.control)) {
        setFormValid(false);
      }
    }
    setForecast(formInitialState.current);
  }

  function handleChange(event) {
    const fieldName = event.target.name;
    const value = event.target.value;

    const updatedPart = { [fieldName]: value };
    const updateForecast = Object.assign({}, forecast, updatedPart);
    setForecast(updateForecast);
    setFormChanged(true);
    validateForecast(updateForecast);
  }

  function isEditOfForecast() {
    const editedForecast = formInitialState.current;
    return !!editedForecast && !!editedForecast.id;
  }

  function isReadAccess(control) {
    return control === 'READ' || control === 'NONE';
  }

  function buttonClickCallback(button) {
    setLoading(true);
    switch (button.id) {
      case 0:
        setFormChanged(false);
        saveForecast(forecast)
          .then(response => {
            if (!!response.data && 'WARNING' === response.data.status) {
              forecastService.showOkMessage(response.data.messages[0]);
            } else {
              forecastService.showSuccessMessage('FORECAST_SAVE_SUCCESS');
            }
            forecastService.loadForecasts();
          })
          .then(
            () => {
              forecastService.cancelTab();
              setLoading(false);
            },
            error => {
              setError(error);
              forecastService.loadForecasts();
              setLoading(false);
            }
          );

        break;
      case 1:
        setFormChanged(false);
        updateForecast(forecast).then(
          () => {
            forecastService.showSuccessMessage('FORECAST_UPDATE_SUCCESS');
            forecastService.loadForecasts().then(() => {
              const f = forecastService.getForecastById(forecast.id);
              formInitialState.current = f;
              setForecast(f);
              getActualsEndPeriods(plan.mongoid, forecast.id).then(response => setActualsEndPeriod(response.data));
              forecastService.updateTabAfterSave(f);
              setLoading(false);
            });
          },
          error => {
            setError(error);
            setLoading(false);
          }
        );
        break;
      case 2:
        forecastService.cancelTab();
        setLoading(false);
        break;
    }
  }

  function validateForecast(forecast) {
    let valid = true;
    if (!forecast.name || forecast.name.length > 250) {
      valid = false;
    } else if (!forecast.actualsVersionId) {
      valid = false;
    } else if (!forecast.budgetVersionId) {
      valid = false;
    } else if (!forecast.forecastSecurity && !isEditOfForecast()) {
      valid = false;
    } else if (!!forecast.description && forecast.description.length > 512) {
      valid = false;
    }
    setFormValid(valid);
  }

  function headerRightSection() {
    return (
      <div>
        <p className="planHeader">
          <strong>{t('FORECAST_HEADER_PLAN')}:</strong> {plan.name}
        </p>
      </div>
    );
  }

  function getTitle() {
    if (isEditOfForecast()) {
      return forecast.name;
    } else {
      return t('FORECAST_NEW');
    }
  }

  let title = getTitle();

  let securityComponent;
  if (!securityEnabled) {
    securityComponent = <p></p>;
  } else if (!isEditOfForecast()) {
    securityComponent = (
      <p>
        <i>{t('FORECAST_FORM_COPY_SECURITY_ONLY_FOR_ADMINS')}</i>
      </p>
    );
    // temporarily disabled as hotfix of CORE-2375. should be enabled early 2023. Delete this if it's here for more than year...:)
    //
    // securityComponent = (
    // 	<div className="row">
    // 		<div className="col-xs-12">
    // 			<label className="control-label">
    // 				<span>{t('FORECAST_FORM_COPY_SECURITY')} <sup className="form-required-icon"><i className="fa fa-asterisk"></i></sup></span>
    // 				<TooltipComponent t={t} i18nKey="FORECAST_FORM_COPY_SECURITY_TOOLTIP"></TooltipComponent>
    // 			</label>
    // 		</div>
    // 		<div className="col-xs-12">
    // 			<div className="form-group">
    // 				<Dropdown name="forecastSecurity" placeholder={t('FORECAST_FORM_COPY_SECURITY_PLACEHOLDER')}
    // 						  options={securityDropdownValues.current} value={forecast.forecastSecurity}
    // 						  onChange={handleChange} disabled={isEditOfForecast()}></Dropdown>
    // 			</div>
    // 		</div>
    // 	</div>
    // );
  } else {
    securityComponent = (
      <p>
        <i>{t('FORECAST_FORM_COPY_SECURITY_DISABLED')}</i>
      </p>
    );
  }

  let periodDropdownTemplate;
  if (forecast.isRollingForecast) {
    periodDropdownTemplate = <div></div>;
    if (isEditOfForecast() && !forecast.endPeriodActuals) {
      forecast.endPeriodActuals = actualsEndPeriods[0];
    }
  } else {
    if ((isEditOfForecast() && !forecast.endPeriodActuals) || (forecast.isCopyMode && !forecast.endPeriodActuals)) {
      forecast.endPeriodActuals = actualsEndPeriods[0];
    } else if (!isEditOfForecast() && !forecast.endPeriodActuals) {
      forecast.endPeriodActuals = actualsEndPeriods[actualsEndPeriods.length - 1];
    }

    periodDropdownTemplate = (
      <div className="row">
        <div className="col-xs-12">
          <label className="control-label">
            <span>
              {t('FORECAST_ACTUALS_PERIOD_DROPDOWN')}{' '}
              <sup className="form-required-icon">
                <i className="fa fa-asterisk"></i>
              </sup>
            </span>
            <TooltipComponent t={t} i18nKey="ACTUALS_END_PERIOD_TOOLTIP"></TooltipComponent>
          </label>
          <div className="form-group">
            <DropdownPM
              name="endPeriodActuals"
              placeholder={t('FORECAST_SELECT_PERIODS_PLACEHOLDER')}
              options={actualsEndPeriods}
              value={forecast.endPeriodActuals}
              onChange={handleChange}></DropdownPM>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {isLoading ? <Loader /> : ''}
      <SectionHeader icon="icon-forecast-section" title={title} rightSectionFunction={headerRightSection}></SectionHeader>
      <div className="profile-form container-fluid scrollable">
        <form id="forecast_form">
          <div className="row">
            <div className="col-xs-12">
              <label className="control-label">
                {t('FORECAST_FORM_NAME')}{' '}
                <sup className="form-required-icon">
                  <i className="fa fa-asterisk"></i>
                </sup>
              </label>
            </div>
            <div className="col-xs-8">
              <div className="form-group">
                <input
                  className="form-control"
                  type="text"
                  name="name"
                  value={forecast.name}
                  disabled={isReadAccess(forecast.control)}
                  onChange={handleChange}
                  required={true}
                />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12">
              <label className="control-label">
                <span>
                  {t('FORECAST_FORM_DESCRIPTION')} ({t('FORECAST_FORM_OPTIONAL')})
                </span>
              </label>
            </div>
            <div className="col-xs-8">
              <div className="form-group">
                <textarea
                  className="form-control"
                  name="description"
                  rows="4"
                  value={forecast.description}
                  onChange={handleChange}
                  disabled={isReadAccess(forecast.control)}></textarea>
              </div>
            </div>
          </div>
          <hr />
          <div className="row">
            <div className="col-xs-12">
              <label className="control-label">
                <h5>{t('FORECAST_FORM_COPY_DATA')}</h5>
              </label>
            </div>
            <div className="col-xs-2">
              <label className="control-label">
                <span>
                  {t('FORECAST_FORM_COPY_DATA_ACTUALS')}{' '}
                  <sup className="form-required-icon">
                    <i className="fa fa-asterisk"></i>
                  </sup>
                </span>
                <TooltipComponent t={t} i18nKey="FORECAST_FORM_COPY_DATA_ACTUALS_TOOLTIP"></TooltipComponent>
              </label>
              <div className="form-group">
                <DropdownPM
                  name="actualsVersionId"
                  placeholder={t('FORECAST_FORM_COPY_DATA_ACTUALS_PLACEHOLDER')}
                  options={actuals}
                  value={forecast.actualsVersionId}
                  onChange={handleChange}
                  disabled={isEditOfForecast()}></DropdownPM>
              </div>
            </div>
            <div className="col-xs-2">
              <label className="control-label">
                <span>
                  {t('FORECAST_FORM_COPY_DATA_BUDGET')}{' '}
                  <sup className="form-required-icon">
                    <i className="fa fa-asterisk"></i>
                  </sup>
                </span>
                <TooltipComponent t={t} i18nKey="FORECAST_FORM_COPY_DATA_BUDGET_TOOLTIP"></TooltipComponent>
              </label>
              <div className="form-group">
                <DropdownPM
                  name="budgetVersionId"
                  placeholder={t('FORECAST_FORM_COPY_DATA_BUDGET_PLACEHOLDER')}
                  options={budgets}
                  value={forecast.budgetVersionId}
                  onChange={handleChange}
                  disabled={isEditOfForecast()}></DropdownPM>
              </div>
            </div>
          </div>
          <p>
            <i>{t('FORECAST_FORM_COPY_DATA_BUDGET_HELP')}</i>
          </p>
          {securityComponent}
          <hr />
          <div className="row">
            <div className="col-xs-12">
              <label className="control-label">
                <h5 className="forecast-select-periods">{t('FORECAST_SELECT_PERIODS')}</h5>
              </label>
            </div>
            <div className="col-xs-2">
              <label className="control-label">
                <span>
                  {t('ROLLING_FORECAST_LABEL')}{' '}
                  <sup className="form-required-icon">
                    <i className="fa fa-asterisk"></i>
                  </sup>
                </span>
                <TooltipComponent t={t} i18nKey="ROLLING_FORECAST_TOOLTIP"></TooltipComponent>
              </label>
              <div className="form-group">
                <InputSwitchPM name="isRollingForecast" checked={forecast.isRollingForecast} onValueChange={handleChange} />
              </div>
            </div>
          </div>
          {periodDropdownTemplate}
        </form>
      </div>
      <ApiErrorMessage error={error}></ApiErrorMessage>
      <SectionFooter buttonConfig={buttonConfig} clickCallback={buttonClickCallback}></SectionFooter>
    </div>
  );
}